import {
  Box,
  Center,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useRef } from 'react';

import { AssignmentTemplate } from './AssignmentTemplate';
import { assignmentTableContentWidth } from './utils';

/**
 * @returns an array of x random integers between two numbers, x being the possibleSelections param
 */
const getSkeletonWidthsCollection = ({
  minWidth,
  maxWidth,
  numberOfRows,
  numberOfColumns,
}: {
  minWidth: number;
  maxWidth: number;
  numberOfRows: number;
  numberOfColumns: number;
}) => {
  const minInt = Math.ceil(minWidth);
  const maxInt = Math.floor(maxWidth);
  return _.map(Array(numberOfRows), () => {
    return _.times(numberOfColumns, () =>
      Math.floor(Math.random() * (maxInt - minInt + 1) + minInt),
    );
  });
};

const AssignmentSkeletonLoadingTableCell = ({ width }: { width: number }) => {
  return (
    <Td>
      <Center>
        <Skeleton height="16px" width={width} />
      </Center>
    </Td>
  );
};

const AssignmentSkeletonLoadingTableRow = ({
  tableTitle,
  rowIndex,
  numberOfColumns,
  widths,
}: {
  tableTitle: string;
  rowIndex: number;
  numberOfColumns: number;
  widths: number[][];
}) => {
  const widthsForRow = widths[rowIndex] || widths[0];
  return (
    <Tr fontSize="13px">
      <Td>
        <Skeleton height="16px" width="16px" />
      </Td>
      {_.map(Array(numberOfColumns), (_item, columnIndex) => {
        return (
          <React.Fragment
            key={`assignment-skeleton-table-cell-${tableTitle}-${rowIndex}-${columnIndex}`}
          >
            <AssignmentSkeletonLoadingTableCell
              width={widthsForRow[columnIndex]}
            />
          </React.Fragment>
        );
      })}
    </Tr>
  );
};

export const AssignmentSkeletonLoadingTable = ({
  tableTitle,
  numberOfRows,
  numberOfColumns,
}: {
  tableTitle?: string;
  numberOfRows: number;
  numberOfColumns: number;
}) => {
  const skeletonColumnWidth = (assignmentTableContentWidth - 50) / 4;
  const skeletonWidthsByRow = getSkeletonWidthsCollection({
    minWidth: 100,
    maxWidth: 200,
    numberOfRows,
    numberOfColumns,
  });
  const skeletonWidthsRef = useRef(skeletonWidthsByRow);
  return (
    <Box marginBottom="30px">
      {tableTitle && (
        <Text as="h2" fontSize="2xl" mb="15px" fontWeight="500">
          {tableTitle}
        </Text>
      )}
      <TableContainer borderWidth="1px" padding="0.75rem" maxHeight="750px">
        <Table style={{ tableLayout: 'fixed' }}>
          <Thead bg="white">
            <Tr>
              <Th bg="white" width="50px">
                <Skeleton height="16px" width="16px" />
              </Th>
              <Th bg="white" width={skeletonColumnWidth}>
                <Center>
                  <Skeleton height="16px" width={100} />
                </Center>
              </Th>
              <Th bg="white" width={skeletonColumnWidth}>
                <Center>
                  <Skeleton height="16px" width={200} />
                </Center>
              </Th>
              <Th bg="white" width={skeletonColumnWidth}>
                <Center>
                  <Skeleton height="16px" width={150} />
                </Center>
              </Th>
              <Th bg="white" width={skeletonColumnWidth}>
                <Center>
                  <Skeleton height="16px" width={225} />
                </Center>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(Array(numberOfRows), (_item, rowIndex) => {
              return (
                <React.Fragment
                  key={`assignment-skeleton-table-row-${tableTitle}-${rowIndex}`}
                >
                  <AssignmentSkeletonLoadingTableRow
                    tableTitle={tableTitle ?? _.uniqueId()}
                    rowIndex={rowIndex}
                    numberOfColumns={numberOfColumns}
                    widths={skeletonWidthsRef.current}
                  />
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export const AssignmentSkeletonLoadingTables = () => {
  return (
    <>
      <AssignmentSkeletonLoadingTable
        tableTitle="Preview receivables to assign"
        numberOfRows={7}
        numberOfColumns={4}
      />
      <AssignmentSkeletonLoadingTable
        tableTitle="Preview receivables to leave on balance sheet"
        numberOfRows={7}
        numberOfColumns={4}
      />
      <AssignmentSkeletonLoadingTable
        tableTitle="Previously unassigned receivables"
        numberOfRows={7}
        numberOfColumns={4}
      />
      <AssignmentSkeletonLoadingTable
        tableTitle="Previously assigned receivables"
        numberOfRows={7}
        numberOfColumns={4}
      />
    </>
  );
};

export const AssignmentLoadingSkeleton = ({
  dataTestId,
  editMode = false,
}: {
  dataTestId: string;
  editMode?: boolean;
}) => {
  return (
    <Box height="100%" width="100%">
      <AssignmentTemplate editMode={editMode}>
        <Skeleton data-testid={dataTestId} height="80vh" mt="32px" />
      </AssignmentTemplate>
    </Box>
  );
};
